import React, { useState, useEffect } from 'react';
import './App.css';
import { buildings, fetchRoomPositions, fetchPositionDetail, updatePositionCounts, updateDeceasedName, PositionDetail } from './models/BuildingData';

function App() {
  const [selectedBuilding, setSelectedBuilding] = useState<number | null>(null);
  const [selectedFloor, setSelectedFloor] = useState<number | null>(null);
  const [selectedRoom, setSelectedRoom] = useState<number | null>(null);
  const [selectedPosition, setSelectedPosition] = useState<string | null>(null);
  const [roomPositions, setRoomPositions] = useState<any[][]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [positionDetail, setPositionDetail] = useState<PositionDetail | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editCounts, setEditCounts] = useState<string>('0');
  const [isEditingName, setIsEditingName] = useState<boolean>(false);
  const [editDeceasedName, setEditDeceasedName] = useState<string>('');
  const [showMessage, setShowMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // 获取当前选中的建筑信息
  const currentBuilding = selectedBuilding !== null 
    ? buildings.find(b => b.id === selectedBuilding) 
    : null;
  
  // 获取当前选中的楼层信息
  const currentFloor = (currentBuilding && selectedFloor !== null) 
    ? currentBuilding.floors.find(f => f.id === selectedFloor) 
    : null;
  
  // 获取当前选中的存放室信息
  const currentRoom = (currentFloor && selectedRoom !== null) 
    ? currentFloor.rooms.find(r => r.id === selectedRoom) 
    : null;

  // 当选中的存放室改变时，获取格位数据
  useEffect(() => {
    if (selectedBuilding !== null && selectedFloor !== null && selectedRoom !== null) {
      setIsLoading(true);
      
      fetchRoomPositions(selectedBuilding, selectedFloor, selectedRoom)
        .then(data => {
          setRoomPositions(data);
          setIsLoading(false);
        })
        .catch(error => {
          console.error('获取数据失败:', error);
          setIsLoading(false);
        });
    } else {
      setRoomPositions([]);
    }
  }, [selectedBuilding, selectedFloor, selectedRoom]);

  // 当选中格位改变时，获取格位详细信息
  useEffect(() => {
    if (selectedPosition) {
      fetchPositionDetail(selectedPosition)
        .then(data => {
          setPositionDetail(data);
          if (data) {
            setEditCounts(data.counts ? data.counts.toString() : '0');
            setEditDeceasedName(data.deceased_name || '');
          }
        })
        .catch(error => {
          console.error('获取格位详细信息失败:', error);
          setPositionDetail(null);
        });
    } else {
      setPositionDetail(null);
      setIsEditing(false);
      setIsEditingName(false);
    }
  }, [selectedPosition]);

  // 获取建筑颜色 - 统一为蓝色
  const getBuildingColor = (): string => {
    return '#3498db'; // 统一所有建筑为蓝色
  };

  // 处理建筑选择
  const handleBuildingClick = (buildingId: number) => {
    if (selectedBuilding === buildingId) {
      setSelectedBuilding(null);
      setSelectedFloor(null);
      setSelectedRoom(null);
      setSelectedPosition(null);
    } else {
      setSelectedBuilding(buildingId);
      setSelectedFloor(null);
      setSelectedRoom(null);
      setSelectedPosition(null);
    }
  };

  // 处理楼层选择
  const handleFloorClick = (floorId: number) => {
    if (selectedFloor === floorId) {
      setSelectedFloor(null);
      setSelectedRoom(null);
      setSelectedPosition(null);
    } else {
      setSelectedFloor(floorId);
      setSelectedRoom(null);
      setSelectedPosition(null);
    }
  };

  // 处理存放室选择
  const handleRoomClick = (roomId: number) => {
    if (selectedRoom === roomId) {
      setSelectedRoom(null);
      setSelectedPosition(null);
    } else {
      setSelectedRoom(roomId);
      setSelectedPosition(null);
    }
  };

  // 处理格位选择
  const handlePositionClick = (positionId: string) => {
    if (selectedPosition === positionId) {
      setSelectedPosition(null);
    } else {
      setSelectedPosition(positionId);
    }
  };

  // 处理骨灰盅数量编辑
  const handleEditCounts = () => {
    setIsEditing(true);
  };
  
  // 处理骨灰盅数量变更
  const handleCountsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // 允许空字符串或非负整数
    if (value === '' || /^\d+$/.test(value)) {
      setEditCounts(value);
    }
  };
  
  // 处理故者姓名编辑
  const handleEditDeceasedName = () => {
    setIsEditingName(true);
  };
  
  // 处理故者姓名变更
  const handleDeceasedNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditDeceasedName(e.target.value);
  };
  
  // 显示消息提示
  const showNotification = (message: string) => {
    setShowMessage(message);
    setTimeout(() => {
      setShowMessage(null);
    }, 3000);
  };
  
  // 保存骨灰盅数量
  const handleSaveCounts = () => {
    if (selectedPosition && positionDetail) {
      setIsSaving(true);
      // 将字符串转换为数字
      const countsValue = editCounts === '' ? 0 : parseInt(editCounts, 10);
      updatePositionCounts(selectedPosition, countsValue)
        .then(updatedData => {
          if (updatedData) {
            // 更新本地数据
            setPositionDetail(updatedData);
            
            // 更新房间格位数据中的骨灰盅数量
            const countsValue = editCounts === '' ? 0 : parseInt(editCounts, 10);
            const updatedPositions = roomPositions.map(row => 
              row.map(pos => 
                pos.id === selectedPosition ? { ...pos, counts: countsValue } : pos
              )
            );
            setRoomPositions(updatedPositions);
            
            // 退出编辑模式
            setIsEditing(false);
            
            // 显示成功提示
            showNotification(`格位 ${selectedPosition} 的骨灰盅数量已更新为 ${editCounts} 个`);
          }
        })
        .catch(error => {
          console.error('保存骨灰盅数量失败:', error);
          showNotification('保存骨灰盅数量失败: ' + error.message);
        })
        .finally(() => {
          setIsSaving(false);
        });
    }
  };
  
  // 取消编辑
  const handleCancelEdit = () => {
    if (positionDetail) {
      setEditCounts(positionDetail.counts ? positionDetail.counts.toString() : '0');
    }
    setIsEditing(false);
  };
  
  // 取消故者姓名编辑
  const handleCancelNameEdit = () => {
    if (positionDetail) {
      setEditDeceasedName(positionDetail.deceased_name || '');
    }
    setIsEditingName(false);
  };
  
  // 保存故者姓名
  const handleSaveDeceasedName = () => {
    if (selectedPosition && positionDetail) {
      setIsSaving(true);
      updateDeceasedName(selectedPosition, editDeceasedName)
        .then(updatedData => {
          if (updatedData) {
            // 更新本地数据
            setPositionDetail(updatedData);
            
            // 退出编辑模式
            setIsEditingName(false);
            
            // 显示成功提示
            showNotification(`格位 ${selectedPosition} 的故者姓名已更新为 ${editDeceasedName}`);
          }
        })
        .catch(error => {
          console.error('保存故者姓名失败:', error);
          showNotification('保存故者姓名失败: ' + error.message);
        })
        .finally(() => {
          setIsSaving(false);
        });
    }
  };

  // 格式化日期显示
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '无';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-CN');
    } catch (error) {
      return dateString;
    }
  };

  return (
    <div className="app-container">
      <header className="app-header">
        <h1>骨灰寄存管理系统</h1>
      </header>
      
      <div className="content-container">
        <div className="sidebar">
          <div className="section">
            <h2>选择建筑</h2>
            <div className="card-container">
              {buildings.map(building => (
                <div 
                  key={building.id}
                  className={`card ${selectedBuilding === building.id ? 'selected' : ''}`}
                  style={{ borderColor: getBuildingColor() }}
                  onClick={() => handleBuildingClick(building.id)}
                >
                  <div 
                    className="card-header" 
                    style={{ backgroundColor: getBuildingColor() }}
                  >
                    {building.name}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {currentBuilding && (
            <div className="section">
              <h2>选择楼层</h2>
              <div className="card-container">
                {currentBuilding.floors.map(floor => (
                  <div 
                    key={floor.id}
                    className={`card ${selectedFloor === floor.id ? 'selected' : ''}`}
                    style={{ borderColor: getBuildingColor() }}
                    onClick={() => handleFloorClick(floor.id)}
                  >
                    <div 
                      className="card-header" 
                      style={{ backgroundColor: getBuildingColor() }}
                    >
                      {floor.id}层
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {currentFloor && (
            <div className="section">
              <h2>选择存放室</h2>
              <div className="card-container">
                {currentFloor.rooms.map(room => (
                  <div 
                    key={room.id}
                    className={`card ${selectedRoom === room.id ? 'selected' : ''}`}
                    style={{ borderColor: getBuildingColor() }}
                    onClick={() => handleRoomClick(room.id)}
                  >
                    <div 
                      className="card-header" 
                      style={{ backgroundColor: getBuildingColor() }}
                    >
                      {room.id}室
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {selectedBuilding && selectedFloor && selectedRoom && (
            <div className="info-section">
              <h2>格位编号规则</h2>
              <div className="info-content">
                <p>格位编号共7位数字，组成规则：</p>
                <ul>
                  <li><span>第1位：</span>建筑编号（1怡然楼，2怡安楼，3怡乐楼）</li>
                  <li><span>第2位：</span>楼层编号（1-3层）</li>
                  <li><span>第3位：</span>存放室编号（1-5室）</li>
                  <li><span>第4-5位：</span>排号（01-11排，自下而上）</li>
                  <li><span>第6-7位：</span>列号（01-60列，自左向右）</li>
                </ul>
                <p className="example">
                  例：<span>1311025</span> 表示怡然楼(1)三层(3)一室(1)第十排(10)第二十五列(25)的格位
                </p>
              </div>
            </div>
          )}
        </div>
        
        <div className="main-content">
          {selectedBuilding && selectedFloor && selectedRoom ? (
            <div className="position-grid-container">
              <h2>{currentBuilding?.name} {selectedFloor}层 {selectedRoom}室 格位一览</h2>
              <div className="position-legend">
                <div className="legend-item">
                  <div className="legend-color unsold"></div>
                  <span>未售出</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color sold"></div>
                  <span>已销售</span>
                </div>
                <div className="legend-item info">
                  <span className="legend-info">7位数字 = 完整格位编号</span>
                </div>
                <div className="legend-item info">
                  <span className="legend-info">盅数 = 骨灰盅数量</span>
                </div>
              </div>
              
              {isLoading ? (
                <div className="loading-message">正在加载格位数据...</div>
              ) : roomPositions.length > 0 ? (
                <div className="position-grid">
                  {/* 将排序反转，从下往上排列 */}
                  {[...roomPositions].reverse().map((row, reversedIndex) => {
                    const actualRowNumber = roomPositions.length - reversedIndex;
                    
                    return (
                      <div key={reversedIndex} className="position-row">
                        <div className="row-number">{actualRowNumber}排</div>
                        <div className="position-cells">
                          {row.map(position => (
                            <div 
                              key={position.id}
                              className={`position-cell ${selectedPosition === position.id ? 'selected' : ''} ${position.isSold ? 'sold' : 'unsold'}`}
                              onClick={() => handlePositionClick(position.id)}
                              title={`格位编号: ${position.id}`}
                            >
                              <span className="position-id">{position.id}</span>
                              {position.counts > 0 && (
                                <span className="position-count">{position.counts}盅</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="no-data-message">
                  未找到格位数据，请检查数据库连接或联系管理员
                </div>
              )}
              
              {selectedPosition && positionDetail && (
                <div className="selected-info">
                  <h3>格位详细信息</h3>
                  <div className="position-id-display">
                    <span className="position-id-label">灵位编号</span>
                    <span className="position-id-value">{positionDetail.spirit_id}</span>
                  </div>
                  <div className="detail-grid">
                    <div className="detail-row">
                      <div className="detail-label">位置：</div>
                      <div className="detail-value">{positionDetail.position || '无'}</div>
                    </div>
                    <div className="detail-row">
                      <div className="detail-label">状态：</div>
                      <div className={`detail-value ${positionDetail.sale_status === '已销售' ? 'sold' : 'unsold'}`}>
                        {positionDetail.sale_status}
                      </div>
                    </div>
                    <div className="detail-row">
                      <div className="detail-label">价格：</div>
                      <div className="detail-value">{positionDetail.price ? `￥${positionDetail.price}` : '未设置'}</div>
                    </div>
                    {positionDetail.sale_status === '已销售' && (
                      <>
                        <div className="detail-row">
                          <div className="detail-label">故者姓名：</div>
                          <div className="detail-value">
                            {isEditingName ? (
                              <div className="edit-controls">
                                <input 
                                  type="text" 
                                  value={editDeceasedName} 
                                  onChange={handleDeceasedNameChange}
                                  className="name-input"
                                  disabled={isSaving}
                                />
                                <div className="edit-buttons">
                                  <button 
                                    onClick={handleSaveDeceasedName} 
                                    className={isSaving ? "save-button saving" : "save-button"}
                                    disabled={isSaving}
                                  >
                                    {isSaving ? "保存中..." : "保存"}
                                  </button>
                                  <button 
                                    onClick={handleCancelNameEdit} 
                                    className="cancel-button"
                                    disabled={isSaving}
                                  >
                                    取消
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                {positionDetail.deceased_name || '无'}
                                <button onClick={handleEditDeceasedName} className="edit-button">
                                  修改
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="detail-row">
                          <div className="detail-label">客户姓名：</div>
                          <div className="detail-value">{positionDetail.customer_name || '无'}</div>
                        </div>
                        <div className="detail-row">
                          <div className="detail-label">购位日期：</div>
                          <div className="detail-value">{formatDate(positionDetail.purchase_date)}</div>
                        </div>
                        <div className="detail-row">
                          <div className="detail-label">管理费状态：</div>
                          <div className={`detail-value ${positionDetail.payment_status === '欠费' ? 'arrears' : 'normal'}`}>
                            {positionDetail.payment_status || '正常'}
                          </div>
                        </div>
                      </>
                    )}
                    <div className="detail-row">
                      <div className="detail-label">骨灰盅数量：</div>
                      <div className="detail-value">
                        {isEditing ? (
                          <div className="edit-controls">
                            <input 
                              type="number" 
                              min="0" 
                              value={editCounts} 
                              onChange={handleCountsChange}
                              className="counts-input"
                              disabled={isSaving}
                            />
                            <div className="edit-buttons">
                              <button 
                                onClick={handleSaveCounts} 
                                className={isSaving ? "save-button saving" : "save-button"}
                                disabled={isSaving}
                              >
                                {isSaving ? "保存中..." : "保存"}
                              </button>
                              <button 
                                onClick={handleCancelEdit} 
                                className="cancel-button"
                                disabled={isSaving}
                              >
                                取消
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            {positionDetail.counts || 0} 个
                            <button onClick={handleEditCounts} className="edit-button">
                              修改
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="welcome-message">
              <h2>欢迎使用骨灰寄存管理系统</h2>
              <p>请在左侧选择建筑、楼层和存放室，查看格位信息</p>
              {selectedBuilding && <p>已选择：{buildings.find(b => b.id === selectedBuilding)?.name}</p>}
              {selectedFloor && <p>已选择：{selectedFloor}层</p>}
            </div>
          )}
        </div>
      </div>
      <footer className="app-footer">
        <p>骨灰寄存管理系统 © {new Date().getFullYear()}</p>
      </footer>
      
      {/* 提示消息 */}
      {showMessage && (
        <div className="notification-message">
          {showMessage}
        </div>
      )}
    </div>
  );
}

export default App;
